/**
 * Client Portal V2 - Invoices List Page
 * ======================================
 * Payment control with variance flagging.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import ClientLayout from "@/components/client/ClientLayout";
import {
  ClientKpiCard, ClientEmptyState, ClientLoadingState, 
  ClientErrorState, ClientInvoiceVarianceBadge
} from "@/components/client/ClientComponents";
import { 
  Receipt, Search, ChevronRight, Clock, 
  CheckCircle, AlertTriangle
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ClientInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 25, total_pages: 1 });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get("status") || "";
  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      params.set("page", page.toString());
      params.set("page_size", "25");

      const res = await apiClient.get(`/api/client/invoices?${params}`);
      
      if (res.data?.success) {
        setInvoices(res.data.data || []);
        setMeta(res.data.meta || {});
      }
    } catch (err) {
      console.error("Failed to load invoices:", err);
      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }
      setError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    fetchInvoices();
  }, [fetchInvoices, navigate]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateFilter("search", formData.get("search"));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  };

  const invoicesWithVariance = invoices.filter(i => i.has_variance).length;

  return (
    <ClientLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
        <p className="text-slate-500 mt-1">Track payments and identify variances</p>
      </div>

      {/* Variance Alert */}
      {invoicesWithVariance > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 font-medium">
            {invoicesWithVariance} invoice{invoicesWithVariance > 1 ? "s" : ""} with variance detected
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => updateFilter("status", "issues")}
          >
            Review
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <ClientKpiCard
          title="Total Invoices"
          value={meta.total}
          icon={Receipt}
          color="teal"
        />
        <ClientKpiCard
          title="With Variance"
          value={invoicesWithVariance}
          icon={AlertTriangle}
          color={invoicesWithVariance > 0 ? "amber" : "green"}
          alert={invoicesWithVariance > 0}
          onClick={() => updateFilter("status", "issues")}
        />
        <ClientKpiCard
          title="Pending"
          value={invoices.filter(i => i.status === "Pending" || i.status === "Under Review").length}
          icon={Clock}
          color="blue"
        />
        <ClientKpiCard
          title="Paid"
          value={invoices.filter(i => i.status === "Paid").length}
          icon={CheckCircle}
          color="green"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="search"
                placeholder="Search by reference, job..."
                defaultValue={search}
                className="pl-10"
              />
            </div>
          </form>

          <Select value={status || "all"} onValueChange={(v) => updateFilter("status", v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="issues">With Variance</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
            </SelectContent>
          </Select>

          {(status || search) && (
            <Button
              variant="ghost"
              onClick={() => setSearchParams({})}
              className="shrink-0"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Invoices List */}
      {loading ? (
        <ClientLoadingState message="Loading invoices..." />
      ) : error ? (
        <ClientErrorState message={error} onRetry={fetchInvoices} />
      ) : invoices.length === 0 ? (
        <ClientEmptyState
          icon={Receipt}
          title="No invoices found"
          description={status || search ? "Try adjusting your filters" : "No invoices have been submitted yet"}
        />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-2">Invoice Ref</div>
            <div className="col-span-2">Job</div>
            <div className="col-span-2">Supplier</div>
            <div className="col-span-1">Approved</div>
            <div className="col-span-1">Invoice</div>
            <div className="col-span-2">Variance</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {invoices.map((invoice) => (
              <Link
                key={invoice.id}
                to={`/client-v2/invoices/${invoice.id}`}
                className={`block hover:bg-slate-50 transition-colors ${invoice.has_variance ? "bg-amber-50/50" : ""}`}
                data-testid={`invoice-row-${invoice.id}`}
              >
                {/* Desktop Row */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 items-center">
                  <div className="col-span-2">
                    <p className="font-medium text-slate-900">{invoice.invoice_reference}</p>
                    <p className="text-xs text-slate-500">{formatDate(invoice.submitted_at)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-700 truncate">{invoice.job_reference || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-700 truncate">{invoice.supplier_name}</p>
                  </div>
                  <div className="col-span-1">
                    <p className="text-slate-600">{formatCurrency(invoice.approved_amount)}</p>
                  </div>
                  <div className="col-span-1">
                    <p className="font-medium text-slate-900">{formatCurrency(invoice.invoice_amount)}</p>
                  </div>
                  <div className="col-span-2">
                    <ClientInvoiceVarianceBadge 
                      variance={invoice.variance} 
                      variancePercent={invoice.variance_percent} 
                    />
                  </div>
                  <div className="col-span-1">
                    <Badge className={
                      invoice.status === "Paid" ? "bg-green-100 text-green-700" :
                      invoice.status === "Disputed" ? "bg-red-100 text-red-700" :
                      "bg-slate-100 text-slate-700"
                    }>
                      {invoice.status}
                    </Badge>
                  </div>
                  <div className="col-span-1 text-right">
                    <ChevronRight className="w-5 h-5 text-slate-400 inline-block" />
                  </div>
                </div>

                {/* Mobile Card */}
                <div className="md:hidden p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-900">{invoice.invoice_reference}</p>
                      <p className="text-sm text-slate-600">{invoice.supplier_name}</p>
                    </div>
                    <Badge className={
                      invoice.status === "Paid" ? "bg-green-100 text-green-700" :
                      "bg-slate-100 text-slate-700"
                    }>
                      {invoice.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Job: {invoice.job_reference || "-"}</span>
                    <span className="font-semibold text-slate-900">{formatCurrency(invoice.invoice_amount)}</span>
                  </div>
                  {invoice.has_variance && (
                    <div className="mt-2">
                      <ClientInvoiceVarianceBadge 
                        variance={invoice.variance} 
                        variancePercent={invoice.variance_percent} 
                      />
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {meta.total_pages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {meta.page} of {meta.total_pages} ({meta.total} invoices)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => updateFilter("page", String(meta.page - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => updateFilter("page", String(meta.page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </ClientLayout>
  );
}
